import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Typography, InputBase, IconButton, Avatar,
  ListItemButton, ListItemIcon,
  Drawer, BottomNavigation, BottomNavigationAction, useMediaQuery,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import GroupsIcon from "@mui/icons-material/Groups";
import LightModeIcon from "@mui/icons-material/LightMode";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DescriptionIcon    from "@mui/icons-material/Description";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";
import GridViewIcon from "@mui/icons-material/Apps";
import SettingsEthernetIcon from "@mui/icons-material/SettingsEthernet";
// ─── Sub-views ────────────────────────────────────────────────────────────────
import LibraryView from "./LibraryView";
import PeopleView  from "./PeopleView";
import CompanyView from "./CompanyView";
import MatrixView from "./MatrixView";
import FormsView from "./FormsView";
import ArchiveView from "./ArchiveView";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { useLogout } from '../api/reactQueryHook/useAuth';
import LoadingOverlay from './LoadingOverlay';


import { useSelector } from 'react-redux';
import { ReduxRootStateType } from '../redux/store';

import { ThemeProvider } from '@mui/material';
import { darkTheme, lightTheme } from '../utils/customTheme';
import DarkModeIcon from "@mui/icons-material/DarkMode";

// Add at the top of Dashboard.tsx, outside all components
import { createContext, useContext } from 'react';



import WelcomeView, { SKILLS_CARDS, SIGNAL_CARDS } from "./WelcomeView";
import { LOGIN_AGENTS, svgSize } from "./login/loginAgents";
import ConnectionSettingsView from "./ConnectionSettingsView";
import { useGetPartnerCompanies } from '../api/reactQueryHook/useAuth';
import { track, identify } from '../utils/MixpanelHelper';

// ─── Icons ────────────────────────────────────────────────────────────────────
import SkillsModeuleIcon  from "../assets/logo/app_logo/SkillsAgentIcon.png";
import SignalsModeuleIcon from "../assets/logo/app_logo/SignalsAgentIcon.png";
import SalesModeuleIcon   from "../assets/logo/app_logo/SalesAgentIcon.png";


export const ThemeContext = createContext<{
  isDark: boolean;
  setIsDark: (val: boolean) => void;
}>({
  isDark: true,
  setIsDark: () => {},
});


// ─── Types ────────────────────────────────────────────────────────────────────
interface Module {
  key: string;
  label: string;
  iconUrl?: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────
const MODULES: Module[] = [
  { key: "skills", label: "Skills Agent",  iconUrl: SkillsModeuleIcon  },
  { key: "signal", label: "Signal Agent",  iconUrl: SignalsModeuleIcon },
  { key: "sales",  label: "Sales Agent",   iconUrl: SalesModeuleIcon   },
];

// Maps a Dashboard module key to the matching login-roller wordmark id so the switcher
// reuses the same vector labels as the login page (the names are pre-rendered SVG paths,
// not a webfont). Note the login wordmark is "signals" where the module key is "signal".
const WORDMARK_AGENT_ID: Record<string, string> = { skills: "skills", signal: "signals", sales: "sales" };


// ─── Theme-aligned colour tokens ─────────────────────────────────────────────
// All values derived from customTheme.ts palette / component overrides.
// REPLACE your static COLORS object with this function
// Shell colors now come from the theme's custom_colors (single source in customTheme.ts).
// Same getColors(isDark) signature, so all call sites are unchanged.
const getColors = (isDark: boolean) => (isDark ? darkTheme : lightTheme).custom_colors;

const FONT_FAMILY = "'Public Sans', 'Montserrat', arial, sans-serif"; // typography.allVariants
const LETTER_SPACING = "0.1rem";                                       // typography.allVariants

const slideUpKeyframes = `
  @keyframes slideUp {
    from { transform: translateY(16px); opacity: 0; }
    to   { transform: translateY(0px);  opacity: 1; }
  }
`;

// ─── Agent Banner ─────────────────────────────────────────────────────────────
const AgentBanner: React.FC<{ label: string; iconUrl?: string }> = ({ label, iconUrl }) => (
  <Box sx={{ height: 20, width: "100%", display: "flex", alignItems: "center", flexShrink: 0 }}>
    {iconUrl ? (
      <img
        src={iconUrl}
        alt={label}
        style={{ width: "100%", paddingLeft: 5, paddingRight: 20, height: 20, objectFit: "fill" }}
      />
    ) : (
      <Typography sx={{
        pl: "5px", pr: "20px",
        fontSize: "0.75rem", fontWeight: 700,
        fontFamily: "'Public Sans', 'Montserrat', arial, sans-serif",
        letterSpacing: "0.12em", textTransform: "uppercase",
        background: "linear-gradient(180deg, #1778C7 0%, #46A0EB 100%)",
        WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
        lineHeight: "20px", whiteSpace: "nowrap",
      }}>
        {label}
      </Typography>
    )}
  </Box>
);

// ─── Company Switcher ─────────────────────────────────────────────────────────
const CompanySwitcher: React.FC<{
  onCompanyChange: (id: number) => void;
  initialCompanyId: number;
  initialCompanyName: string;
  partnerCompanies?: { company_id: number; company_name: string }[];
}> = ({ onCompanyChange, initialCompanyId, initialCompanyName, partnerCompanies = [] }) => {
  const [selected, setSelected] = useState({ company_id: initialCompanyId, company_name: initialCompanyName });
  const [open, setOpen]         = useState(false);
  const ref                     = React.useRef<HTMLDivElement>(null);
  const { isDark } = useContext(ThemeContext);
  const COLORS = getColors(isDark);
  const userInfo = useSelector((state: ReduxRootStateType) => state.user.user_info);
  const allCompanies = [
    { company_id: initialCompanyId, company_name: initialCompanyName },
    ...partnerCompanies,
  ];

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
     <ThemeProvider theme={isDark ? darkTheme : lightTheme}>
    <Box ref={ref} sx={{ position: "relative", mx: 1, mb: 1 }}>
      <Box
        onClick={() => setOpen(p => !p)}
        sx={{
          display: "flex", alignItems: "center", gap: 1,
          px: 1.5, py: 1, borderRadius: "6px",
          backgroundColor: "transparent", cursor: "pointer",
          "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)" },
          transition: "background-color 0.15s",
        }}
      >
        <Box sx={{ flex: 1, overflow: "hidden" }}>
          <Typography noWrap sx={{
            color: COLORS.activeNavSolid, fontSize: "0.8rem", fontWeight: 600,
            fontFamily: FONT_FAMILY, letterSpacing: LETTER_SPACING, textTransform: "uppercase",
          }}>
            {selected.company_name}
          </Typography>
        </Box>
        <KeyboardArrowDownIcon sx={{
          color: COLORS.activeNavSolid, fontSize: "1.5rem", flexShrink: 0,
          transform: open ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 0.2s",
        }} />
      </Box>

      {open && (
        <Box sx={{
          position: "absolute", top: "100%", left: 0, right: 0,
          mt: 0.5, borderRadius: "6px",
          backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}`,
          boxShadow: isDark ? "0 8px 24px rgba(0,0,0,0.5)" : "0 4px 12px rgba(0,0,0,0.12)",
          overflow: "hidden", zIndex: 999,
          animation: "slideUp 0.2s cubic-bezier(0.22, 1, 0.36, 1) forwards",
        }}>
          {allCompanies.map(company => {
            const isActive = company.company_id === selected.company_id;
            return (
              <Box
                key={company.company_id}
                onClick={() => {
                  track('Company Switched', {
                    'From Company': selected.company_name,
                    'To Company':   company.company_name,
                    'Name':         `${userInfo?.first_name} ${userInfo?.last_name}`,
                    'Email':        userInfo?.email_id,
                    'Company Name': userInfo?.company_name,
                  });
                  setSelected({ company_id: company.company_id, company_name: company.company_name });
                  onCompanyChange(company.company_id);
                  setOpen(false);
                }}
                sx={{
                  display: "flex", alignItems: "center", gap: 1.5,
                  px: 1.5, py: 1, cursor: "pointer",
                  backgroundColor: isActive ? COLORS.card : "transparent",
                  "&:hover": { backgroundColor: COLORS.cardHover },
                  transition: "background-color 0.1s",
                }}
              >
                <BusinessIcon sx={{ color: isActive ? COLORS.activeNavSolid : COLORS.iconMuted, fontSize: "1rem" }} />
                <Typography sx={{
                  flex: 1,
                  color: isActive ? COLORS.activeNavSolid : COLORS.textPrimary,
                  fontSize: "0.8rem", fontWeight: isActive ? 600 : 400,
                  fontFamily: FONT_FAMILY, letterSpacing: LETTER_SPACING,
                }}>
                  {company.company_name}
                </Typography>
                {isActive && <CheckIcon sx={{ color: COLORS.activeNavSolid, fontSize: "1rem" }} />}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
    </ThemeProvider>
  );
};

// ─── Assets Section ───────────────────────────────────────────────────────────
const AssetsSection: React.FC<{
  activeNav: string;
  setActiveNav: (key: string) => void;
  agentKey: string;
}> = ({ activeNav, setActiveNav, agentKey }) => {
  const [open, setOpen] = useState(true);
  const { isDark } = useContext(ThemeContext);
  const COLORS = getColors(isDark);
  const allItems = [
    { label: "BPR Files",     icon: <DescriptionIcon />,  key: "files"        },
    { label: "Forms",         icon: <DescriptionIcon />,  key: "manage-forms" },
    { label: "People",        icon: <GroupsIcon />,        key: "people"       },
  ];
  const items = agentKey === "signal"
    ? allItems.filter(i => i.key === "files" || i.key === "people")
    : allItems;

  return (
    <ThemeProvider theme={isDark ? darkTheme : lightTheme}>
    <Box sx={{
      mt: 1, borderRadius: "10px", mx: "10px", mr: "10px",
      backgroundColor: COLORS.assetsBg, border: `1px solid ${COLORS.border}`,
      overflow: "hidden",
    }}>
      <Box
        onClick={() => setOpen(p => !p)}
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: "18px", py: 2, cursor: "pointer" }}
      >
        <Typography sx={{
          color: "text.primary", fontSize: "0.8rem", fontWeight: 700,
          letterSpacing: "0.06em", fontFamily: FONT_FAMILY,
        }}>
          ASSETS
        </Typography>
        {open
          ? <KeyboardArrowUpIcon sx={{ color: COLORS.activeNavSolid, fontSize: "1.1rem" }} />
          : <KeyboardArrowDownIcon sx={{ color: COLORS.textSecondary, fontSize: "1.1rem" }} />
        }
      </Box>

      {open && items.map(({ label, icon, key }) => {
        const act = activeNav === key;
        return (
          <ListItemButton
            key={key}
            onClick={() => setActiveNav(key)}
            sx={{
              px: 2, py: 1.5,
              background: act ? COLORS.activeNavGrad : "transparent",
              "&:hover": {
                background: act
                  ? "linear-gradient(180deg, #1366a9 0%, #2790e8 100%)"
                  : "action.hover",
              },
              transition: "background 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, color: act ? "#FFFFFF" : COLORS.textPrimary }}>
              {React.cloneElement(icon, { sx: { fontSize: "16px" } })}
            </ListItemIcon>
            <Typography sx={{
              fontSize: "12px", fontWeight: act ? 600 : 400,
              fontFamily: FONT_FAMILY, letterSpacing: LETTER_SPACING,
              color: act ? "#FFFFFF" : COLORS.textPrimary, textTransform: "uppercase",
            }}>
              {label}
            </Typography>
          </ListItemButton>
        );
      })}
    </Box>
    </ThemeProvider>
  );
};

// ─── Metrix Section ───────────────────────────────────────────────────────────
const MetrixCenterSection: React.FC<{
  activeNav: string;
  setActiveNav: (key: string) => void;
  onMatrixClick: () => void;
}> = ({ activeNav, setActiveNav, onMatrixClick }) => {
  const [open, setOpen] = useState(true);
  const { isDark } = useContext(ThemeContext);
  const COLORS = getColors(isDark);
  const items = [
    { label: "Acknowledgement Matrix", icon: <NoteAltOutlinedIcon />, key: "acknowledgement"  },
    { label: "Archive",                icon: <Inventory2OutlinedIcon />, key: "archive"        },
  ];

  return (
    <ThemeProvider theme={isDark ? darkTheme : lightTheme}>
    <Box sx={{
      mt: 1, borderRadius: "10px", mx: "10px", mr: "10px",
      backgroundColor: COLORS.assetsBg, border: `1px solid ${COLORS.border}`,
      overflow: "hidden",
    }}>
      <Box
        onClick={() => setOpen(p => !p)}
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: "18px", py: 2, cursor: "pointer" }}
      >
        <Typography sx={{
          color: "text.primary", fontSize: "0.8rem", fontWeight: 700,
          letterSpacing: "0.06em", fontFamily: FONT_FAMILY, textTransform: "uppercase",
        }}>
          SUBMISSIONS
        </Typography>
        {open
          ? <KeyboardArrowUpIcon sx={{ color: COLORS.activeNavSolid, fontSize: "1.1rem" }} />
          : <GridViewIcon sx={{ color: COLORS.activeNavSolid, fontSize: "1.1rem" }} />
        }
      </Box>

      {open && items.map(({ label, icon, key }) => {
        const act = activeNav === key;
        return (
          <ListItemButton
            key={key}
            onClick={() => key === "acknowledgement" ? onMatrixClick() : setActiveNav(key)}
            sx={{
              px: 2, py: 1.5,
              background: act ? COLORS.activeNavGrad : "transparent",
              "&:hover": {
                background: act
                  ? "linear-gradient(180deg, #1366a9 0%, #2790e8 100%)"
                  : "action.hover",
              },
              transition: "background 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, color: act ? "#FFFFFF" : COLORS.textPrimary }}>
              {React.cloneElement(icon, { sx: { fontSize: "16px" } })}
            </ListItemIcon>
            <Typography sx={{
              fontSize: "12px", fontWeight: act ? 600 : 400,
              fontFamily: FONT_FAMILY, letterSpacing: LETTER_SPACING,
              color: act ? "#FFFFFF" : COLORS.textPrimary, textTransform: "uppercase",
            }}>
              {label}
            </Typography>
          </ListItemButton>
        );
      })}
    </Box>
    </ThemeProvider>
  );
};


// ─── Main Dashboard ───────────────────────────────────────────────────────────
const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userInfo = useSelector((state: ReduxRootStateType) => state.user.user_info);

  // Call identify on every session load — covers SSO users and fresh devices
  // where the login mutation's onSuccess never fires.
  useEffect(() => {
    if (userInfo) {
      identify(userInfo.email_id, userInfo.first_name, userInfo.last_name, userInfo.company_name);
    }
  }, [userInfo?.email_id]);

const activeNav = location.pathname === '/' ? 'companies'
  : location.pathname.replace('/', ''); // "people", "library", etc.

const setActiveNav = (key: string) => {
  const path = key === 'companies' ? '/' : `/${key}`;
  navigate(path);
};
  const [selected, setSelected]                   = useState<Module>(MODULES[0]);
  const [switchOpen, setSwitchOpen]               = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(userInfo?.company_id ?? 0);
  const [connectedForms, setConnectedForms] = useState<Record<string, string>>({});
// Add state to control when logout fires
const [triggerLogout, setTriggerLogout] = useState(false);

// Call the hook with enabled driven by state
const { isLoading: isLoggingOut } = useLogout({ enabled: triggerLogout });

const [isDark, setIsDark] = useState<boolean>(() => {
  const saved = localStorage.getItem('bloxscale-theme');
  if (saved) return saved === 'dark';
  return userInfo?.theme !== 'light';
});
const COLORS = getColors(isDark);
const isMobile = useMediaQuery('(max-width: 768px)');
const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [showWelcome, setShowWelcome] = useState(() => {
    const knownRoutes = ['/people', '/files', '/manage-forms', '/acknowledgement', '/archive', '/connection-settings'];
    return !knownRoutes.includes(location.pathname);
  });
useGetPartnerCompanies();
const getWelcomeCards = (agentKey: string) => {
  if (agentKey === "signal") return SIGNAL_CARDS;
  return SKILLS_CARDS;
};

const renderSidebarContent = () => (
  <>
    {/* Agent Banner */}
    <Box sx={{ px: 0.5, pb: 1.5 }}>
      <Box
        onClick={() => {
          if (selected.key === "skills" || selected.key === "signal") { setShowWelcome(true); setActiveNav(""); }
          setSwitchOpen(p => !p);
        }}
        sx={{
          display: "flex", alignItems: "flex-start", gap: 1,
          py: 0.5, cursor: "pointer", "&:hover": { opacity: 0.85 },
          borderRadius: "6px", px: "10px", flex: 1,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <AgentBanner label={selected.label} iconUrl={selected.iconUrl} />
        </Box>
        <KeyboardArrowDownIcon sx={{
          color: COLORS.activeNavSolid, fontSize: "1.2rem", flexShrink: 0,
          transform: switchOpen ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 0.25s",
        }} />
      </Box>
    </Box>

    {/* Nav */}
    <Box sx={{ flex: 1, pt: 1.5, px: "10px", pr: "10px" }}>
      <CompanySwitcher
        onCompanyChange={setSelectedCompanyId}
        initialCompanyId={userInfo?.company_id ?? 0}
        initialCompanyName={userInfo?.company_name ?? ""}
        partnerCompanies={userInfo?.partner_company ?? []}
      />
      {selected.key === "sales" ? (
        <Box sx={{
          mt: 2, mx: "10px", px: 2, py: 3, borderRadius: "10px",
          backgroundColor: COLORS.assetsBg, border: `1px solid ${COLORS.border}`,
          display: "flex", flexDirection: "column", alignItems: "center", gap: 1,
        }}>
          <Typography sx={{
            color: COLORS.activeNavSolid, fontSize: "0.85rem", fontWeight: 700,
            fontFamily: FONT_FAMILY, letterSpacing: LETTER_SPACING, textTransform: "uppercase",
          }}>
            Coming Soon
          </Typography>
          <Typography sx={{
            color: COLORS.textSecondary, fontSize: "0.75rem",
            fontFamily: FONT_FAMILY, letterSpacing: LETTER_SPACING, textAlign: "center",
          }}>
            Sales Agent is not yet available.
          </Typography>
        </Box>
      ) : (
        <>
          <AssetsSection
            activeNav={activeNav}
            agentKey={selected.key}
            setActiveNav={(key) => { setActiveNav(key); setShowWelcome(false); setMobileDrawerOpen(false); }}
          />
          {selected.key !== "signal" && (
            <MetrixCenterSection
              activeNav={activeNav}
              setActiveNav={(key) => { setActiveNav(key); setShowWelcome(false); setMobileDrawerOpen(false); }}
              onMatrixClick={() => { setActiveNav("acknowledgement"); setShowWelcome(false); setMobileDrawerOpen(false); }}
            />
          )}
        </>
      )}
    </Box>

    {/* Bottom: User + Logout */}
    <Box sx={{ px: 1.5 }}>
      <Box sx={{
        display: "flex", alignItems: "center", gap: 1.5,
        px: 1.5, py: 1.5, mb: 1, borderRadius: "6px",
        backgroundColor: COLORS.userBg,
      }}>
        <Avatar sx={{
          width: 30, height: 30,
          background: COLORS.activeNavGrad,
          fontSize: "0.75rem", fontFamily: FONT_FAMILY,
        }}>{userInfo?.first_name?.[0] ?? "?"}</Avatar>
        <Box sx={{ flex: 1, overflow: "hidden" }}>
          <Typography noWrap sx={{
            color: COLORS.textPrimary, fontSize: "0.8rem", fontWeight: 500,
            fontFamily: FONT_FAMILY, letterSpacing: LETTER_SPACING,
          }}>{userInfo ? `${userInfo.first_name} ${userInfo.last_name}` : ""}</Typography>
          <Typography sx={{
            color: COLORS.textSecondary, fontSize: "0.7rem",
            fontFamily: FONT_FAMILY, letterSpacing: LETTER_SPACING,
          }}>{userInfo?.mh_admin ? "Admin" : "Standard"}</Typography>
        </Box>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 0.5 }}>
        <IconButton
          size="small"
          onClick={() => { const next = !isDark; setIsDark(next); localStorage.setItem('bloxscale-theme', next ? 'dark' : 'light'); }}
          sx={{ color: isDark ? COLORS.iconMuted : "#fdd835", "&:hover": { color: "#fdd835" } }}
        >
          {isDark ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
        </IconButton>
        <Box
          onClick={() => setTriggerLogout(true)}
          sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: isLoggingOut ? "not-allowed" : "pointer" }}
        >
          <Typography sx={{ color: COLORS.error, fontSize: "0.85rem", fontFamily: FONT_FAMILY, letterSpacing: LETTER_SPACING }}>
            Logout
          </Typography>
          <LogoutIcon sx={{ color: COLORS.error, fontSize: "1rem" }} />
        </Box>
      </Box>
    </Box>

    {/* Switch Agent Overlay */}
    {switchOpen && (
      <Box sx={{
        position: "absolute", bottom: 0, left: 0, right: 0,
        mx: 1, mb: 5, p: "14px 8px 10px",
        borderRadius: "18px", backgroundColor: COLORS.card,
        // Subtle accent-tinted ring + lift, mirroring the login roller card.
        border: `1px solid ${COLORS.accent}38`,
        boxShadow: `0 0 0 1px ${COLORS.accent}14, 0 8px 32px rgba(0,0,0,0.5)`,
        overflow: "hidden", zIndex: 10,
        animation: "slideUp 0.28s cubic-bezier(0.22, 1, 0.36, 1) forwards",
      }}>
        <Box sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          px: "8px", pb: "8px",
        }}>
          <Typography sx={{
            color: COLORS.iconMuted, fontSize: "11px", fontWeight: 700,
            fontFamily: FONT_FAMILY, letterSpacing: "0.12em", textTransform: "uppercase",
          }}>
            Switch Agent
          </Typography>
          <IconButton size="small" onClick={() => setSwitchOpen(false)}
            sx={{ color: COLORS.iconMuted, p: 0.25, "&:hover": { color: COLORS.textPrimary } }}>
            <CloseIcon sx={{ fontSize: "16px" }} />
          </IconButton>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          {MODULES.map((mod, i) => {
            const isActive = mod.key === selected.key;
            const wordmark = LOGIN_AGENTS.find(a => a.id === WORDMARK_AGENT_ID[mod.key]);
            // Match the login roller's typeface exactly by reusing its vector wordmark; fall
            // back to text only if a module has no corresponding login wordmark. Inactive rows
            // use the accent blue at ~50% alpha (8-digit hex) so they read as a dimmed blue —
            // matching the reference — rather than a flat grey.
            const wordmarkColor = isActive ? COLORS.accent : `${COLORS.accent}80`;
            return (
              <Box
                key={mod.key}
                onClick={() => {
                  if (mod.key === "skills" || mod.key === "signal") {
                    setShowWelcome(true);
                    setActiveNav("");
                  } else {
                    setShowWelcome(false);
                  }
                  track('Agent Switched', {
                    'From Agent':   selected.label,
                    'To Agent':     mod.label,
                    'Name':         `${userInfo?.first_name} ${userInfo?.last_name}`,
                    'Email':        userInfo?.email_id,
                    'Company Name': userInfo?.company_name,
                  });
                  setSelected(mod);
                  setSwitchOpen(false);
                }}
                sx={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  px: "14px", py: "11px", borderRadius: "12px", cursor: "pointer",
                  // Active row reads as a filled pill with an inset accent ring; others stay flat.
                  backgroundColor: isActive ? `${COLORS.accent}1F` : "transparent",
                  boxShadow: isActive ? `inset 0 0 0 1px ${COLORS.accent}2E` : "none",
                  "&:hover": { backgroundColor: isActive ? `${COLORS.accent}29` : COLORS.cardHover },
                  transition: "background-color 0.1s",
                  animation: `slideUp 0.3s cubic-bezier(0.22, 1, 0.36, 1) ${i * 0.05}s both`,
                }}
              >
                {wordmark ? (
                  <svg
                    viewBox={wordmark.viewBox}
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ height: 14, width: svgSize(wordmark.viewBox, 14).w, display: "block", flexShrink: 0 }}
                  >
                    <path d={wordmark.path} fill={wordmarkColor} />
                  </svg>
                ) : (
                  <Typography noWrap sx={{
                    color: wordmarkColor,
                    fontSize: "0.8rem", fontWeight: isActive ? 700 : 600,
                    fontFamily: FONT_FAMILY, letterSpacing: "0.1em", textTransform: "uppercase",
                  }}>
                    {mod.label}
                  </Typography>
                )}
                {/* Glowing dot marks the current agent (only rendered on the active row). */}
                {isActive && (
                  <Box sx={{
                    width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
                    backgroundColor: COLORS.accent,
                    boxShadow: `0 0 8px 2px ${COLORS.accent}99`,
                  }} />
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    )}
  </>
);

return (
  <ThemeContext.Provider value={{ isDark, setIsDark }}>
    <ThemeProvider theme={isDark ? darkTheme : lightTheme}>
      <>
        <style>{slideUpKeyframes}</style>
        <LoadingOverlay isLoading={isLoggingOut} />


        <Box sx={{
          display: "flex", flexDirection: "column",
          height: "100vh", width: "100vw",
          backgroundColor: COLORS.bg,
          fontFamily: FONT_FAMILY,
          overflow: "hidden", border: `1px solid ${COLORS.border}`, borderRadius: "8px",
        }}>

          {/* ── Mobile Top Bar ── */}
          {isMobile && (
            <Box sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              px: 2, py: 1.5, flexShrink: 0,
              backgroundColor: COLORS.sidebar,
              borderBottom: `1px solid ${COLORS.border}`,
            }}>
              <IconButton onClick={() => setMobileDrawerOpen(true)} sx={{ color: COLORS.textPrimary, p: 0.5 }}>
                <MenuIcon />
              </IconButton>
              <Typography sx={{
                color: COLORS.activeNavSolid, fontSize: "0.9rem", fontWeight: 700,
                fontFamily: FONT_FAMILY, letterSpacing: LETTER_SPACING,
              }}>
                {userInfo?.company_name ?? "BloxScale"}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <IconButton size="small" onClick={() => { const next = !isDark; setIsDark(next); localStorage.setItem('bloxscale-theme', next ? 'dark' : 'light'); }}
                  sx={{ color: isDark ? COLORS.iconMuted : "#fdd835" }}>
                  {isDark ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
                </IconButton>
                <Avatar sx={{ width: 30, height: 30, background: COLORS.activeNavGrad, fontSize: "0.75rem" }}>
                  {userInfo?.first_name?.[0] ?? "?"}
                </Avatar>
              </Box>
            </Box>
          )}

          {/* ── Sidebar + Content Row ── */}
          <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>

            {/* ── Desktop Sidebar ── */}
            <Box sx={{
              width: 325, minWidth: 325,
              backgroundColor: COLORS.sidebar,
              display: { xs: "none", md: "flex" }, flexDirection: "column",
              py: 5, borderRight: `1px solid ${COLORS.border}`,
              position: "relative",
            }}>
              {renderSidebarContent()}
            </Box>

            {/* ── Mobile Drawer ── */}
            <Drawer
              anchor="left"
              open={mobileDrawerOpen}
              onClose={() => setMobileDrawerOpen(false)}
              slotProps={{ paper: {
                sx: {
                  width: 300,
                  backgroundColor: COLORS.sidebar,
                  pt: 3,
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  overflow: "hidden",
                }
              }}}
            >
              {renderSidebarContent()}
            </Drawer>

            {/* ── Main Content ── */}
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", position: "relative", m: { xs: 0, md: 1 } }}>

              {selected.key === "sales" ? (
                <Box sx={{
                  flex: 1, display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center", gap: 2,
                  overflow: "hidden",
                }}>
                  <Typography sx={{
                    color: COLORS.activeNavSolid, fontSize: "2rem", fontWeight: 700,
                    fontFamily: FONT_FAMILY, letterSpacing: LETTER_SPACING, textTransform: "uppercase",
                  }}>
                    Coming Soon
                  </Typography>
                  <Typography sx={{
                    color: COLORS.textSecondary, fontSize: "1rem",
                    fontFamily: FONT_FAMILY, letterSpacing: LETTER_SPACING, textAlign: "center",
                  }}>
                    Sales Agent is not yet implemented.
                  </Typography>
                </Box>
              ) : showWelcome ? (
                <WelcomeView
                  cards={getWelcomeCards(selected.key)}
                  onNavigate={(target) => {
                    if (target === "forms")       { setActiveNav("manage-forms");    setShowWelcome(false); }
                    if (target === "people")      { setActiveNav("people");          setShowWelcome(false); }
                    if (target === "submissions") { setActiveNav("acknowledgement"); setShowWelcome(false); }
                    if (target === "files")       { setActiveNav("files");           setShowWelcome(false); }
                  }}
                />
              ) : activeNav === "files" ? (
                <LibraryView selectedCompanyId={selectedCompanyId} agentKey={selected.key}
                  connectedForms={connectedForms}
                  onConnectForm={(fileKey, formName) => setConnectedForms(p => ({ ...p, [fileKey]: formName }))}
                  onDisconnectForm={(fileKey) => setConnectedForms(p => { const n = { ...p }; delete n[fileKey]; return n; })}/>
              ) : activeNav === "manage-forms" ? (
                <FormsView selectedCompanyId={selectedCompanyId} />
              ) : activeNav === "people" ? (
                <PeopleView selectedCompanyId={selectedCompanyId} />
              ) : activeNav === "acknowledgement" ? (
                <MatrixView selectedCompanyId={selectedCompanyId} />
              ) : activeNav === "archive" ? (
                <ArchiveView selectedCompanyId={selectedCompanyId} />
              ) : activeNav === "connection-settings" ? (
                <ConnectionSettingsView />
              ) : (
                <CompanyView />
              )}

            </Box>
          </Box>

          {/* ── Mobile Bottom Navigation ── */}
          {isMobile && (
            <BottomNavigation
              value={activeNav}
              onChange={(_, newVal) => {
                if (newVal === "menu") { setMobileDrawerOpen(true); return; }
                setActiveNav(newVal);
                setShowWelcome(false);
              }}
              sx={{
                flexShrink: 0,
                backgroundColor: COLORS.sidebar,
                borderTop: `1px solid ${COLORS.border}`,
                "& .MuiBottomNavigationAction-root": { color: COLORS.textSecondary, minWidth: 0 },
                "& .Mui-selected": { color: COLORS.activeNavSolid },
              }}
            >
              <BottomNavigationAction label="Files" value="files" icon={<DescriptionIcon sx={{ fontSize: "1.2rem" }} />} />
              <BottomNavigationAction label="Forms" value="manage-forms" icon={<NoteAltOutlinedIcon sx={{ fontSize: "1.2rem" }} />} />
              <BottomNavigationAction label="People" value="people" icon={<GroupsIcon sx={{ fontSize: "1.2rem" }} />} />
              <BottomNavigationAction label="Menu" value="menu" icon={<MenuIcon sx={{ fontSize: "1.2rem" }} />} />
            </BottomNavigation>
          )}

        </Box>
      </>
    </ThemeProvider>
  </ThemeContext.Provider>
);
};

export default Dashboard;